<template lang="pug">
book-contents-background(:subject="subject")
.bookContentsComponent
  .main
    .leftSide
      ui-user-item.item(
        :user-item="userItem"
        :isClickable="false"
        :showInfo="true"
        :showFavorite="isAuthenticated"
        @action:click-on-info="onInfoClick"
      )
      .footer
        template(v-if="getIsInBasket(userItem.nomenklatura)")
          .textBasket Уже в корзине
        template(v-else)
          ui-price(
            v-if="!userItemIsBought"
            variant="button"
            text="В корзину за"
            :price="userItem.price"
            :old-price="userItem.oldPrice"
            @buttonClick="addToBasket(userItem.nomenklatura)"
          )
        template(v-if="!userItemIsBought")
          .text При покупке предоставляется доступ на 1 год ко всем учебникам предмета для 1 класса
          //.text Материалы для 2 класса станут доступны 01.07.2022
        template(v-if="isAuthenticated && subscription?.isDemo && !userItemIsBought")
          .text Демо-доступ до {{ subscription.expires }}
        template(v-else-if="userItemIsBought")
          .text Подписка активна до {{ userItemExpires }}
          //.text Материалы для 2 класса станут доступны 01.07.2022
    .rightSide
      .title Содержание
      .contents
        .chapter(
          v-for="chapter in book.chapters"
        )
          .title {{ chapter.label }}
          .lessons
            .lesson(
              v-for="lesson in chapter.lessons"
              :class="{_selected: hasProgress && isLessonViewed(lesson.id)}"
              @click="(isAuthenticated && subscription.isDemo || userItemIsBought) && onLessonClick(lesson.id)"
            )
              .label
                .text {{ lesson.label }}
                .hint(v-if="!isAuthenticated || ((subscription.isNone || subscription.isOver) && !userItemIsBought)")
                  | Нет доступа к уроку. Активируйте демо&#8209;доступ или приобретите предмет.
                template(v-if="lesson.isAdditional")
                  .starIconWrapper
                    ui-svg-icon(name="star")
              .page {{ lesson.page }}
</template>

<script>
import { computed, inject } from 'vue'
import BookContentsModal from './BookContentsModal'
import BookContentsBackground from './BookContentsBackground'
import UserItem from '@/models/UserItem'
import moment from 'moment'

export default {
  components: {
    BookContentsModal,
    BookContentsBackground
  },
  props: {
    userItem: {
      type: UserItem,
      required: true
    }
  },
  setup (props) {
    const store = inject('store')
    const book = computed(() => props.userItem.book)
    const subject = computed(() => book.value.subject)
    const childBook = computed(() => book.value.progress)
    const hasProgress = computed(() => !!childBook.value)
    const isAuthenticated = computed(() => store.user.isAuthenticated)
    const subscription = computed(() => store.user.profile?.subscription)
    const subscriptions = computed(() => store.user.profile?.subscriptions)

    const subscriptionsItem = computed(() =>
      subscriptions.value?.find(el => el.nomenklatura === props.userItem.nomenklatura)
    )
    const userItemIsBought = computed(() => subscriptionsItem.value?.bought)
    const userItemExpires = computed(() => {
      return subscriptionsItem.value?.expires
        ? moment(subscriptionsItem.value?.expires).format('DD.MM.YYYY')
        : null
    })

    const onPurchaseButtonClick = () => {
      isAuthenticated.value ? props.userItem.goToPurchaseLink() : store.user.login()
    }

    const onInfoClick = () => {
      store.modalQueue.push({
        key: 'bookinfo',
        component: BookContentsModal,
        props: {
          book: props.userItem.book
        }
      })
    }

    const getIsInBasket = (nomenklatura) => {
      return store.basket.data?.items.find(o => o.nomenklatura === nomenklatura)
    }

    const addToBasket = (nomenklatura) => {
      if (!getIsInBasket(nomenklatura)) {
        store.basket.addProducts([nomenklatura])
      }
    }

    return {
      getIsInBasket,
      addToBasket,
      book,
      subject,
      childBook,
      hasProgress,
      subscription,
      isAuthenticated,
      userItemIsBought,
      userItemExpires,
      onInfoClick,
      onPurchaseButtonClick
    }
  },
  methods: {
    isLessonViewed (id) {
      return this.childBook?.viewedLessonsID?.includes(id) || false
    },
    onLessonClick (id) {
      this.$router.push({ name: 'Lesson', params: { id } })
    }
  }
}
</script>

<style lang="sass" scoped>
.bookContentsComponent
  > .main
    display: flex
    align-items: flex-start
    gap: 8*$u

    > .leftSide
      flex: 0 0 102*$u
      @media (max-width: $XXXLWidth)
        flex: 0 0 84*$u
      @media (max-width: $padWidth)
        flex: 0 0 75*$u
      @media (max-width: $MWidth)
        flex: initial

      .footer
        .textBasket
          white-space: nowrap
          cursor: default
          height: 11.5*$u
          padding: 0 3*$u
          margin-bottom: 3*$u
          display: flex
          width: 32*$u
          justify-content: center
          align-items: center
          background-color: $cHtBlue
          color: $cDarkGrey
          border-radius: 2.5*$u
        .button
          ~ .text
            margin-top: 3*$u
        .text
          color: $cDarkGrey
          @include font('t16-regular')
          + .text
            margin-top: 3*$u

    > .rightSide
      flex: 1 0 1px
      border-radius: $brL
      background-color: $cWhite
      padding: 8*$u

      > .title
        @include font('h1')
        margin-bottom: 5*$u

      > .contents
        display: flex
        flex-flow: column
        @include gap(5*$u, "column")

        > .chapter
          border-radius: $brM
          background-color: $cElements
          padding: 5*$u 7.5*$u
          @media (max-width: $XLWidth)
            padding: 4*$u

          > .title
            @include font('h2')
            margin-bottom: 2.5*$u

          > .lessons
            display: flex
            flex-flow: column
            align-items: stretch
            justify-content: space-between
            @include gap(1.25*$u, "column")

            > .lesson
              @include font('t18-regular')
              display: flex
              @include gap(7*$u)
              align-items: flex-start
              justify-content: space-between
              cursor: pointer

              &._selected
                color: $cDarkBlue

              &:hover
                color: $cBlue
                > .label
                  > .hint
                    opacity: 1

              > .label
                display: flex
                align-items: center
                position: relative

                > .hint
                  position: absolute
                  transition: $animationSpeed
                  pointer-events: none
                  opacity: 0
                  background: $cWhite
                  padding: 3*$u 4*$u
                  left: 0
                  top: 100%
                  width: 77*$u
                  z-index: 1
                  border-radius: $brS
                  @include font('t14-regular')
                  line-height: 1.25
                  color: $cText
                  box-shadow: $boxShadow

                > .starIconWrapper
                  width: 3.5*$u
                  height: 3.5*$u
                  margin-left: 1*$u
                  color: $cYellow

@media (max-width: $XLWidth)
  .bookContentsPageComponent
    .main
      .rightSide
        padding: 4*$u

@media (max-width: $MWidth)
  .bookContentsPageComponent
    .main
      flex-direction: column
      align-items: initial
      .rightSide
        max-width: 100%
</style>
